/**
 * Supervisor calendar data (#3671): the tasks of one visible window, and the
 * reschedule write. Uses the existing /tasks API; the calendar window filter is
 * what lets unscheduled deadline tasks through (`dateFrom`/`dateTo` look at
 * scheduledAt only).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import type { TaskListItem } from '@/hooks/useTasks';

/** Server page size cap; the window is fetched page by page up to MAX_PAGES. */
const PAGE_SIZE = 100;
export const MAX_CALENDAR_PAGES = 10;

/** 'all' = every visible task, 'unassigned', or an assignee's user id. */
export type SupervisorAssigneeFilter = string;

export interface SupervisorCalendarWindow {
  from: string;
  to: string;
  assignee: SupervisorAssigneeFilter;
}

interface TaskPage {
  data: TaskListItem[];
  pagination: { nextCursor: string | null; hasMore: boolean };
}

/** Request params. audience=supervisors already returns 'anyone' tasks too; the toggle is client-side. */
export function supervisorCalendarParams(
  range: SupervisorCalendarWindow,
): Record<string, string | number | boolean> {
  return {
    audience: 'supervisors',
    calendarFrom: range.from,
    calendarTo: range.to,
    limit: PAGE_SIZE,
    ...(range.assignee === 'unassigned'
      ? { unassigned: true }
      : range.assignee !== 'all'
        ? { assignedToId: range.assignee }
        : {}),
  };
}

export async function fetchSupervisorCalendarTasks(
  range: SupervisorCalendarWindow,
): Promise<{ tasks: TaskListItem[]; truncated: boolean }> {
  const params = supervisorCalendarParams(range);
  const tasks: TaskListItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_CALENDAR_PAGES; page += 1) {
    const response = await api.get<TaskPage>('/tasks', {
      params: { ...params, ...(cursor ? { cursor } : {}) },
    });
    tasks.push(...response.data);
    if (!response.pagination.hasMore || !response.pagination.nextCursor) {
      return { tasks, truncated: false };
    }
    cursor = response.pagination.nextCursor;
  }
  return { tasks, truncated: true };
}

export function useSupervisorCalendarTasks(
  range: SupervisorCalendarWindow,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: queryKeys.tasks.list({ scope: 'supervisor-calendar', ...range }),
    queryFn: () => fetchSupervisorCalendarTasks(range),
    enabled: options.enabled,
    staleTime: 15 * 1000,
  });
}

export interface RescheduleTaskInput {
  id: string;
  scheduledAt: string;
  /** The updatedAt the calendar showed; sent as If-Match. */
  expectedUpdatedAt: string;
}

/**
 * Move a task. No optimistic cache write: the bar stays put until the server
 * accepts the change. Settled either way, the lists refetch so a refused or
 * stale drop is replaced by the server's current state.
 */
export function useRescheduleCalendarTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledAt, expectedUpdatedAt }: RescheduleTaskInput) =>
      api.patch<TaskListItem>(
        `/tasks/${id}`,
        { scheduledAt },
        { headers: { 'If-Match': `"${expectedUpdatedAt}"` } },
      ),
    onSettled: (_data, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() });
    },
  });
}
